import { Star, TrendingUp } from "lucide-react";
import {
  ShopifyLogo,
  SlackLogo,
  HubSpotLogo,
  NotionLogo,
  CanvaLogo,
  AirbnbLogo,
  ZoomLogo,
  StripeLogo,
  MetaLogo,
} from "./BrandLogos";

/**
 * Business categories we've grown — each card shows a hero image,
 * category name, and a result metric badge.
 */
const BUSINESSES = [
  {
    name: "AC Repair Shops",
    metric: "+3.2x calls",
    img: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Fridge Repair Shops",
    metric: "+180% leads",
    img: "https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Salons & Spas",
    metric: "+220% bookings",
    img: "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Photo Studios",
    metric: "+140% shoots",
    img: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Car Rentals",
    metric: "+4x inquiries",
    img: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Driving Schools",
    metric: "+260% signups",
    img: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Flower Shops",
    metric: "+190% orders",
    img: "https://images.unsplash.com/photo-1561181286-d3fee7d55364?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "PG & Hostels",
    metric: "98% occupancy",
    img: "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Hotels & Resorts",
    metric: "+3.5x direct bookings",
    img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=640&q=80",
  },
  {
    name: "Retail & Other Businesses",
    metric: "+210% footfall",
    img: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=640&q=80",
  },
];

const LOGOS = [
  { name: "Shopify", Icon: ShopifyLogo },
  { name: "Slack", Icon: SlackLogo },
  { name: "HubSpot", Icon: HubSpotLogo },
  { name: "Notion", Icon: NotionLogo },
  { name: "Canva", Icon: CanvaLogo },
  { name: "Airbnb", Icon: AirbnbLogo },
  { name: "Zoom", Icon: ZoomLogo },
  { name: "Stripe", Icon: StripeLogo },
  { name: "Meta", Icon: MetaLogo },
];

function BusinessCard({ b, testId }) {
  return (
    <figure
      className="group mx-2 flex-shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/40 sm:mx-3"
      style={{ width: "min(80vw, 300px)" }}
      data-testid={testId}
    >
      <div className="relative h-44 w-full overflow-hidden sm:h-48">
        <img
          src={b.img}
          alt={b.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur-md">
          <TrendingUp className="h-3 w-3" />
          {b.metric}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-1 text-amber-400">
            {Array.from({ length: 5 }).map((_, j) => (
              <Star key={j} className="h-3 w-3 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <div className="mt-1 font-display text-base font-bold text-white">
            {b.name}
          </div>
        </div>
      </div>
    </figure>
  );
}

export default function Testimonials() {
  const track = [...BUSINESSES, ...BUSINESSES];

  return (
    <section
      id="testimonials"
      className="relative py-14 md:py-20"
      data-testid="testimonials-section"
    >
      <div className="mx-auto max-w-6xl px-5 md:px-6">
        <div className="mb-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-blue-400">
            Loved by founders
          </span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Trusted by 200+ businesses
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-400">
            From local shops to multi-location brands — here are a few industries we've helped scale.
          </p>
        </div>
      </div>

      {/* Full-bleed marquee */}
      <div
        className="group relative"
        data-testid="business-carousel"
        style={{
          maskImage:
            "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
        }}
      >
        <div className="marquee-track flex w-max py-2">
          {track.map((b, i) => (
            <BusinessCard key={i} b={b} testId={`business-card-${i}`} />
          ))}
        </div>
      </div>

      {/* logo strip */}
      <div className="mx-auto mt-12 max-w-6xl px-5 md:px-6">
        <div
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 text-slate-400 sm:gap-x-10"
          data-testid="trust-logos"
        >
          {LOGOS.map(({ name, Icon }) => (
            <div
              key={name}
              title={name}
              className="opacity-60 transition-all duration-300 hover:-translate-y-0.5 hover:text-white hover:opacity-100"
            >
              <Icon className="h-6 w-auto sm:h-7" />
              <span className="sr-only">{name}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .marquee-track {
          animation: scrollBiz 50s linear infinite;
        }
        .group:hover .marquee-track {
          animation-play-state: paused;
        }
        @keyframes scrollBiz {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
}
